import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiClient } from '../../App';
import AdminHeader from '../../components/AdminHeader';
import { 
  Clock, 
  User, 
  UserPlus, 
  UserMinus, 
  Bell, 
  BellOff, 
  Mail, 
  XCircle,
  CheckCircle,
  AlertCircle,
  Filter,
  RefreshCw,
  Calendar,
  Phone,
  MessageSquare
} from 'lucide-react';

// Event type icons and colors
const EVENT_CONFIG = {
  GAME_CREATED: { icon: Calendar, color: 'text-green-600', bg: 'bg-green-100', label: 'Game Created' },
  GAME_CANCELED: { icon: XCircle, color: 'text-red-600', bg: 'bg-red-100', label: 'Game Canceled' },
  INVITE_SENT: { icon: Mail, color: 'text-blue-500', bg: 'bg-blue-100', label: 'Invite Sent' },
  INVITE_ACCEPTED: { icon: CheckCircle, color: 'text-green-500', bg: 'bg-green-100', label: 'Invite Accepted' },
  USER_JOINED: { icon: UserPlus, color: 'text-green-600', bg: 'bg-green-100', label: 'Player Joined' },
  USER_LEFT: { icon: UserMinus, color: 'text-yellow-600', bg: 'bg-yellow-100', label: 'Player Left' },
  WAITLIST_PROMOTED: { icon: UserPlus, color: 'text-purple-600', bg: 'bg-purple-100', label: 'Waitlist Promoted' },
  PLAYER_REMOVED: { icon: UserMinus, color: 'text-red-500', bg: 'bg-red-100', label: 'Player Removed' },
  CONFIRMATION_REQUEST_SENT: { icon: Bell, color: 'text-blue-600', bg: 'bg-blue-100', label: 'Confirmation Sent' },
  USER_CONFIRMED: { icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-100', label: 'User Confirmed' },
  NOTIFICATION_CREATED: { icon: Bell, color: 'text-gray-500', bg: 'bg-gray-100', label: 'Notification Created' },
  NOTIFICATION_SENT: { icon: Bell, color: 'text-green-500', bg: 'bg-green-100', label: 'Notification Sent' },
  NOTIFICATION_SUPPRESSED: { icon: BellOff, color: 'text-orange-500', bg: 'bg-orange-100', label: 'Notification Suppressed' },
};

import { parseUTC } from '../../utils/datetime';

// Format time in user's local timezone with abbreviation
const formatLocalTime = (timestamp) => {
  if (!timestamp) return 'Unknown';
  const date = parseUTC(timestamp);
  
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZoneName: 'short'
  });
};

export default function GameTimeline() {
  const { gameId } = useParams();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [gameInfo, setGameInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [includeNotifications, setIncludeNotifications] = useState(true);
  
  const fetchTimeline = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await apiClient.get(
        `/admin/games/${gameId}/timeline?include_notifications=${includeNotifications}`
      );
      
      setEvents(response.data.events || []);
      setGameInfo({
        title: response.data.game_title,
        status: response.data.game_status,
        event_count: response.data.event_count
      });
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to fetch timeline');
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    if (gameId) {
      fetchTimeline();
    }
  }, [gameId, includeNotifications]);
  
  const renderEventCard = (event, index) => {
    const config = EVENT_CONFIG[event.event_type] || {
      icon: AlertCircle,
      color: 'text-gray-500',
      bg: 'bg-gray-100',
      label: event.event_type
    };
    const IconComponent = config.icon;
    
    return (
      <div key={event.id || index} className="relative pl-8 pb-6" data-testid={`timeline-event-${index}`}>
        {/* Timeline line */}
        {index < events.length - 1 && (
          <div className="absolute left-3 top-8 bottom-0 w-0.5 bg-gray-200" />
        )}
        
        {/* Event dot */}
        <div className={`absolute left-0 w-6 h-6 rounded-full ${config.bg} flex items-center justify-center`}>
          <IconComponent className={`w-3 h-3 ${config.color}`} />
        </div>
        
        {/* Event content */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <span className={`font-medium ${config.color}`}>{config.label}</span>
            <span className="text-xs text-gray-400 flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {formatLocalTime(event.created_at)}
            </span>
          </div>
          
          {/* Actor */}
          {event.actor_username && (
            <div className="flex items-center gap-2 text-sm text-gray-600 mb-1">
              <User className="w-3 h-3" />
              <span>By: <strong>{event.actor_username}</strong></span>
            </div>
          )}
          
          {/* Target */}
          {event.target_username && event.target_user_id !== event.actor_user_id && (
            <div className="flex items-center gap-2 text-sm text-gray-600 mb-1">
              <User className="w-3 h-3" />
              <span>To: <strong>{event.target_username}</strong></span>
            </div>
          )}
          
          {/* Roster count */}
          {event.roster_count_snapshot !== null && event.roster_count_snapshot !== undefined && (
            <div className="text-xs text-gray-400 mt-2">
              Roster: {event.roster_count_snapshot} players
            </div>
          )}
          
          {/* Metadata */}
          {event.metadata && Object.keys(event.metadata).length > 0 && (
            <div className="mt-2 pt-2 border-t border-gray-100">
              <details className="text-xs">
                <summary className="cursor-pointer text-gray-500 hover:text-gray-700">
                  Details
                </summary>
                <pre className="mt-2 p-2 bg-gray-50 rounded text-xs overflow-x-auto">
                  {JSON.stringify(event.metadata, null, 2)}
                </pre>
              </details>
            </div>
          )}
          
          {/* Notification Audit for game_canceled events */}
          {event.notification_audit && event.notification_audit.length > 0 && (
            <div className="mt-3 pt-3 border-t border-gray-100" data-testid="notification-audit">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Notification Audit</p>
              <div className="space-y-2">
                {event.notification_audit.map((ua) => (
                  <div key={ua.user_id} className="p-2.5 bg-gray-50 rounded-lg text-xs" data-testid={`audit-user-${ua.username}`}>
                    <p className="font-semibold text-gray-800 mb-1.5">{ua.username}</p>
                    <div className="flex flex-wrap gap-x-4 gap-y-1">
                      {Object.entries(ua.channel_audit).map(([ch, info]) => {
                        const icon = ch === 'sms' ? <Phone className="w-3 h-3" /> : ch === 'email' ? <Mail className="w-3 h-3" /> : <MessageSquare className="w-3 h-3" />;
                        const statusColor = info.status === 'delivered' ? 'text-green-600' :
                          info.status === 'sent' ? 'text-blue-600' :
                          info.status === 'skipped' ? 'text-amber-600' :
                          info.status === 'failed' ? 'text-red-600' : 'text-gray-500';
                        return (
                          <span key={ch} className="flex items-center gap-1">
                            {icon}
                            <span className="text-gray-500 capitalize">{ch}:</span>
                            <span className={`font-medium capitalize ${statusColor}`}>{info.status}</span>
                            {info.reason && <span className="text-gray-400">– {info.reason.replace(/_/g, ' ')}</span>}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };
  
  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6" data-testid="game-timeline-page">
      <div className="max-w-3xl mx-auto">
        {/* Admin Header */}
        <AdminHeader 
          title="GAME TIMELINE" 
          onRefresh={fetchTimeline}
          refreshing={loading}
        />
        
        {/* Game Info */}
        {gameInfo && (
          <div className="mb-4">
            <p className="text-gray-600">
              {gameInfo.title} 
              <span className={`ml-2 px-2 py-0.5 rounded text-xs ${
                gameInfo.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                gameInfo.status === 'completed' ? 'bg-gray-100 text-gray-700' :
                'bg-green-100 text-green-700'
              }`}>
                {gameInfo.status}
              </span>
            </p>
            <p className="text-xs text-gray-400 mt-1">ID: {gameId}</p>
          </div>
        )}
        
        {/* Filters */}
        <div className="mb-6">
          <div className="bg-white rounded-lg border border-gray-200 p-3 flex items-center gap-4">
            <Filter className="w-4 h-4 text-gray-400" />
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={includeNotifications}
                onChange={(e) => setIncludeNotifications(e.target.checked)}
                className="rounded border-gray-300"
              />
              <span>Include notification events</span>
            </label>
          </div>
        </div>
        
        {/* Timeline */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-6 h-6 text-gray-400 animate-spin" />
          </div>
        ) : error ? (
          <div className="bg-red-50 text-red-700 p-4 rounded-lg">
            {error}
          </div>
        ) : events.length === 0 ? (
          <div className="bg-white rounded-lg border border-gray-200 p-8 text-center text-gray-500">
            No events recorded for this game yet.
          </div>
        ) : (
          <div className="space-y-0">
            {events.map((event, index) => renderEventCard(event, index))}
          </div>
        )}
        
        {/* Event count */}
        {!loading && events.length > 0 && (
          <div className="text-center text-sm text-gray-400 mt-6">
            {events.length} event{events.length !== 1 ? 's' : ''} recorded
          </div>
        )}
      </div>
    </div>
  );
}
